"use client"
import { forwardRef, type CSSProperties, type HTMLAttributes } from "react"
import { type LucideIcon } from "lucide-react"
import {
  POLICY_STATUS_LABELS,
  POLICY_STATUS_ORDER,
  type PoliciesByStatus,
  type PolicyStatus,
} from "../policiesData"
import PoliciesSection from "../PoliciesSection"
import { POLICY_ITEM_APPEARANCE, resolvePolicyStatusAppearances, type PolicyItemAppearance } from "./policyAppearance"

type PoliciesStatusColumnsProps = HTMLAttributes<HTMLDivElement> & {
  grouped: PoliciesByStatus
  onHoverPolicy: (policyId: string | null) => void
  // Fires when the pointer enters a status LABEL (see PoliciesSection's
  // onHoverLabel) — enter-only, with the hovered status.
  onHoverStatusLabel?: (status: PolicyStatus) => void
  compact?: boolean
  reverse?: boolean
  // "columns" (default): statuses side-by-side. "rows": statuses stacked
  // vertically, each with its label on the left — fits beside another block.
  layout?: "columns" | "rows"
  strokeColor?: string
  // Overrides the icon color independently of strokeColor. Falls back to
  // strokeColor when unset (preserving prior behavior).
  iconColor?: string
  strokeStyle?: "solid" | "dashed"
  backgroundColor?: string
  borderRadius?: number
  gap?: number
  strokeWidth?: number
  squareSize?: number
  // Narrows the box width only, keeping its height at squareSize/BOX_SIZE.
  boxWidth?: number
  // Overrides the per-status label text (defaults to POLICY_STATUS_LABELS).
  // Use shorter labels so more boxes fit in a "rows"-layout column.
  statusLabels?: Partial<Record<PolicyStatus, string>>
  // Overrides the "rows" layout label-gutter width (default 96px).
  labelWidth?: number
  // Box outline shape forwarded to PolicyItem ("rounded" default, or "hexagon").
  shape?: "rounded" | "hexagon"
  // When true, boxes wrap instead of overflowing in a narrow column.
  wrapBoxes?: boolean
  // "rows" layout label placement: "left" gutter (default), "top", or "right"
  // (boxes hug the column's left edge, label to their right).
  rowsLabelPosition?: "left" | "top" | "right"
  // "rows" + left label: drop the fixed gutter so the label hugs its word and the
  // boxes start just after it (fitting more per line). See PoliciesSection.
  labelHug?: boolean
  // In labelHug mode: align boxes to the row "start" (default) or "end" (right).
  labelHugAlign?: "start" | "end"
  // Per-status glyph that replaces the text label (lets the gutter shrink).
  statusIcons?: Partial<Record<PolicyStatus, LucideIcon>>
  // When false, the status labels keep their given casing instead of ALL-CAPS.
  labelUppercase?: boolean
  // Overrides the status label font size (default 9px), e.g. "8.5px".
  labelFontSize?: string
  // In labelHug mode, overrides the label→box gap (px). Default 10; HUD 75 uses 6.
  labelHugGapPx?: number
  // Overflowing boxes slide left OVER the label instead of past the column edge.
  // See PoliciesSection. Pair with labelHugGapPx={0}. See HUD 86.
  overlapLabel?: boolean
  // Per-status SHORTER label swapped in when a row's full label + boxes would
  // overflow the column (measured live in PoliciesSection). See HUD 87.
  crowdedStatusLabels?: Partial<Record<PolicyStatus, string>>
  // Drop label characters the boxes would touch, revealing them again as boxes
  // move away (e.g. "Current P"). See PoliciesSection / HUD 88.
  truncateLabelOnOverlap?: boolean
  // Collapse the three statuses into TWO categories — enacted ("current") vs.
  // not-yet-a-policy ("in_discussion" + "off_the_table" merged under the
  // "off_the_table" key). Label/icon/appearance keep using those two keys.
  twoCategory?: boolean
  fillPatternStyle?: CSSProperties
  // Per-status static CSS fill, overriding `fillPatternStyle` per column.
  fillPatternStyleByStatus?: Partial<Record<PolicyStatus, CSSProperties>>
  // Per-status appearance map replacing POLICY_ITEM_APPEARANCE as the base.
  statusAppearance?: Record<PolicyStatus, PolicyItemAppearance>
  // Drop the status glyph inside each box so it reads as a plain square. See HUD 78.
  hideIcon?: boolean
}

// Status order when collapsed to two categories: enacted ("current") first,
// everything-not-yet-a-policy ("off_the_table", which absorbs "in_discussion")
// second.
const TWO_CATEGORY_ORDER: PolicyStatus[] = ["current", "off_the_table"]

// Fold "in_discussion" into "off_the_table" so the panel reads as a binary
// is-it-a-policy-yet split. The "current" bucket is left untouched.
const mergeToTwoCategories = (grouped: PoliciesByStatus): PoliciesByStatus => ({
  current: grouped.current,
  in_discussion: [],
  off_the_table: [...grouped.in_discussion, ...grouped.off_the_table],
})

// Three side-by-side columns ("Current" | "In Discussion" | "Off the Table") of animated policy
// boxes. forwardRef + ...rest so Tooltip's cloneElement can attach its ref and hover handlers.
export const PoliciesStatusColumns = forwardRef<HTMLDivElement, PoliciesStatusColumnsProps>(
  ({ grouped, onHoverPolicy, onHoverStatusLabel, compact = false, reverse = false, layout = "columns", strokeColor, iconColor, strokeStyle, backgroundColor, borderRadius, gap, strokeWidth, squareSize, boxWidth, statusLabels, labelWidth, shape, wrapBoxes, rowsLabelPosition, labelHug, labelHugAlign, statusIcons, labelUppercase, labelFontSize, labelHugGapPx, overlapLabel, crowdedStatusLabels, truncateLabelOnOverlap, twoCategory, fillPatternStyle, fillPatternStyleByStatus, statusAppearance, hideIcon, ...rest }, ref) => {
    const appearances = resolvePolicyStatusAppearances(
      statusAppearance ?? POLICY_ITEM_APPEARANCE,
      { strokeColor, iconColor, strokeStyle, backgroundColor },
    )
    const className = layout === "rows"
      ? "flex w-full flex-col gap-2 overflow-visible"
      : compact
        ? "flex w-full items-start justify-between gap-4 overflow-hidden"
        : "flex w-full items-start justify-between gap-8 overflow-visible"

    const effectiveGrouped = twoCategory ? mergeToTwoCategories(grouped) : grouped
    const baseOrder = twoCategory ? TWO_CATEGORY_ORDER : POLICY_STATUS_ORDER
    const order = reverse ? [...baseOrder].reverse() : baseOrder

    return (
      <div
        ref={ref}
        {...rest}
        className={className}
      >
        {order.map((status) => {
          return (
            <PoliciesSection
              key={status}
              status={status}
              label={statusLabels?.[status] ?? POLICY_STATUS_LABELS[status]}
              policies={effectiveGrouped[status]}
              onHoverPolicy={onHoverPolicy}
              onHoverLabel={onHoverStatusLabel ? () => onHoverStatusLabel(status) : undefined}
              appearance={appearances[status]}
              compact={compact}
              reverse={reverse}
              layout={layout}
              borderRadius={borderRadius}
              gap={gap}
              strokeWidth={strokeWidth}
              squareSize={squareSize}
              boxWidth={boxWidth}
              labelWidth={labelWidth}
              shape={shape}
              wrapBoxes={wrapBoxes}
              rowsLabelPosition={rowsLabelPosition}
              labelHug={labelHug}
              labelHugAlign={labelHugAlign}
              statusIcon={statusIcons?.[status]}
              labelUppercase={labelUppercase}
              labelFontSize={labelFontSize}
              labelHugGapPx={labelHugGapPx}
              overlapLabel={overlapLabel}
              crowdedLabel={crowdedStatusLabels?.[status]}
              truncateLabelOnOverlap={truncateLabelOnOverlap}
              fillPatternStyle={fillPatternStyleByStatus?.[status] ?? fillPatternStyle}
              hideIcon={hideIcon}
            />
          )
        })}
      </div>
    )
  }
)
PoliciesStatusColumns.displayName = "PoliciesStatusColumns"
